import { keyframes, styled } from "goober";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { mobileMedia, useIsMobile } from "../hooks/useIsMobile";
import {
  NAV_TRANSITION_DISTANCE,
  prefersReducedMotion,
  riseInAnimation,
} from "../hooks/useNavTransition";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { ClickButton } from "./ClickButton";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { Input } from "./Input";
import { EASE_OUT, MOTION_BASE_MS } from "./motion";
import { ChatPageSurface } from "./PersonalChats/ChatPageSurface";
import { PressButton } from "./PressButton";
import { DropdownOpener } from "./Settings/shared";
import { Text } from "./Text";

const DEPARTMENTS = [
  "IT",
  "Security",
  "Finance",
  "HR",
  "RevOps",
  "Engineering",
  "Product",
  "Legal",
  "Other",
] as const;

export type Department = (typeof DEPARTMENTS)[number];

const SENIORITIES = [
  "Individual contributor",
  "Manager",
  "Director",
  "VP",
  "C-level / Executive",
];

const Shell = styled("div")`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 12px;
  background: var(--surface-secondary);

  ${mobileMedia} {
    padding: 0;
  }
`;

const Surface = styled(ChatPageSurface)`
  overflow-y: auto;

  ${mobileMedia} {
    border-radius: 0;
    outline: none;
    box-shadow: none;
  }
`;

const Content = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  margin: auto;
  padding: 48px 16px;
  text-align: center;
  animation: ${riseInAnimation};
`;

const Header = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 300px;
`;

const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 360px;
`;

const LabeledField = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

const FieldRow = styled("div")`
  display: flex;
  gap: 12px;

  & > * {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LabelEl = styled("label")`
  padding-left: 8px;
`;

function FieldLabel({
  id,
  htmlFor,
  children,
}: {
  id?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <LabelEl id={id} htmlFor={htmlFor}>
      <Text variant="text-small-md" color="var(--text-primary)">
        {children}
      </Text>
    </LabelEl>
  );
}

const RequiredMark = styled("span")`
  margin-left: 2px;
  font-size: 10px;
  opacity: 0.4;
`;

/* 14px under the global 1.2 mobile zoom renders at 16.8px, above the 16px
   threshold at which iOS Safari auto-zooms the page on field focus. */
const mobileFieldMedia =
  "@media (max-width: 768px) and (hover: none) and (pointer: coarse)";

const FormInput = styled(Input)`
  && {
    min-width: 0;
  }

  ${mobileFieldMedia} {
    && {
      font-size: 14px;
    }
  }
`;

const NativeSelectWrapper = styled("div")`
  position: relative;
`;

const NativeSelect = styled("select")`
  width: 100%;
  height: 32px;
  padding: 0 28px 0 12px;
  border: 0;
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  background: var(--surface-primary);
  color: var(--text-primary);
  font-family: InterVariable;
  font-size: 14px;
  appearance: none;
  color-scheme: light dark;

  &:has(option[value=""]:checked) {
    color: var(--text-secondary);
  }
`;

const NativeSelectChevron = styled("span")`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  opacity: 0.3;
  pointer-events: none;
`;

function SelectDropdown({
  id,
  labelId,
  value,
  options,
  onChange,
}: {
  id: string;
  labelId: string;
  value: string | null;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  if (isMobile) {
    return (
      <NativeSelectWrapper>
        <NativeSelect
          id={id}
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="" disabled hidden>
            Select…
          </option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </NativeSelect>
        <NativeSelectChevron>
          <Icon name="chevron-down" size={12} />
        </NativeSelectChevron>
      </NativeSelectWrapper>
    );
  }
  return (
    <Dropdown
      align="bottom-left"
      onOpenChange={setOpen}
      opener={
        <DropdownOpener
          id={id}
          type="button"
          aria-labelledby={`${labelId} ${id}`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Text
            variant="text-regular"
            color={
              value === null ? "var(--text-secondary)" : "var(--text-primary)"
            }
            ellipsis
          >
            {value ?? "Select…"}
          </Text>
          <span style={{ opacity: 0.3 }}>
            <Icon name="chevron-down" size={12} />
          </span>
        </DropdownOpener>
      }
      sections={[
        {
          items: options.map((option) => ({
            type: "button" as const,
            left: null,
            label: option,
            selected: option === value,
            right:
              option === value ? (
                <Icon name="check" size={12} color="var(--text-secondary)" />
              ) : undefined,
            onClick: () => onChange(option),
          })),
        },
      ]}
    />
  );
}

const InputWithReset = styled("div")`
  position: relative;
  animation: ${fadeSlideIn} var(--motion-base) var(--ease-out);
`;

const ResetButton = styled(ClickButton)`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }
`;

const ConsentGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const CheckRow = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;

  &&:focus-visible {
    outline: none;
  }

  &:focus-visible [data-check-box] {
    box-shadow: 0 0 0 3px var(--border-alpha);
  }
`;

const CheckBoxSlot = styled("span")`
  flex-shrink: 0;
  display: flex;
  border-radius: 50%;
  color: color-mix(in srgb, var(--text-primary) 30%, transparent);

  &[data-checked] {
    color: var(--text-primary);
  }
`;

const fullWidthButton = {
  width: "100%",
  justifyContent: "center",
} as const;

const SubmitButton = styled(Button)`
  &&:disabled {
    opacity: 1;
  }
  &&:disabled > * {
    opacity: 0.4;
  }
`;

export type CompleteProfileData = {
  firstName: string;
  lastName: string;
  companyName: string;
  department: string;
  seniority: string;
  marketingOptIn: boolean;
  privacyAccepted: boolean;
  termsAccepted: boolean;
};

export function CompleteProfile({
  onSubmit,
  initialFirstName = "",
  initialLastName = "",
  initialCompanyName = "",
  companyNameLocked = false,
}: {
  onSubmit: (profile: CompleteProfileData) => void;
  initialFirstName?: string;
  initialLastName?: string;
  initialCompanyName?: string;
  companyNameLocked?: boolean;
}) {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [companyName, setCompanyName] = useState(initialCompanyName);
  const [department, setDepartment] = useState<string | null>(null);
  const [customDepartment, setCustomDepartment] = useState("");
  const [seniority, setSeniority] = useState<string | null>(null);
  const [agreedMarketing, setAgreedMarketing] = useState(false);

  const isOther = department === "Other";
  const customDept = customDepartment.trim();
  const resolvedDepartment = isOther ? customDept : (department ?? "");

  const detailsComplete =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    companyName.trim().length > 0 &&
    resolvedDepartment.length > 0 &&
    seniority !== null;

  const firstNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const otherDeptRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOther) requestAnimationFrame(() => otherDeptRef.current?.focus());
  }, [isOther]);

  const resetDepartment = () => {
    setDepartment(null);
    setCustomDepartment("");
    requestAnimationFrame(() =>
      document.getElementById("profile-department")?.focus()
    );
  };

  const contentRef = useRef<HTMLDivElement>(null);
  const [leaving, setLeaving] = useState(false);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!detailsComplete || seniority === null || leaving) return;
    const profile = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      companyName: companyName.trim(),
      department: resolvedDepartment,
      seniority,
      marketingOptIn: agreedMarketing,
      privacyAccepted: true,
      termsAccepted: true,
    };
    const content = contentRef.current;
    if (!content || prefersReducedMotion()) {
      onSubmit(profile);
      return;
    }
    // The form rises out the way the global nav slides, and the workspace rises in behind it.
    setLeaving(true);
    void content
      .animate(
        [
          { opacity: 1, transform: "none" },
          {
            opacity: 0,
            transform: `translateY(-${NAV_TRANSITION_DISTANCE}px)`,
          },
        ],
        {
          duration: MOTION_BASE_MS,
          easing: EASE_OUT,
          fill: "forwards",
        }
      )
      .finished.then(() => onSubmit(profile));
  };

  return (
    <Shell>
      <Surface>
        <Content ref={contentRef}>
          <Icon name="tines-mark" style={{ width: 28, height: 28 }} />
          <Header>
            <Text
              variant="display-md"
              style={{ fontSize: "22px", lineHeight: "28px" }}
            >
              Welcome to 3B
            </Text>
            <Text
              variant="text-regular"
              color="var(--text-secondary)"
              style={{ fontSize: "13px", lineHeight: "18px" }}
            >
              A few quick details to personalize the experience for you and your
              team.
            </Text>
          </Header>
          <Form onSubmit={submit}>
            <FieldRow>
              <LabeledField>
                <FieldLabel htmlFor="profile-first-name">
                  First name
                  <RequiredMark aria-hidden="true">*</RequiredMark>
                </FieldLabel>
                <FormInput
                  ref={firstNameRef}
                  id="profile-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </LabeledField>
              <LabeledField>
                <FieldLabel htmlFor="profile-last-name">
                  Last name
                  <RequiredMark aria-hidden="true">*</RequiredMark>
                </FieldLabel>
                <FormInput
                  id="profile-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </LabeledField>
            </FieldRow>
            <LabeledField>
              <FieldLabel htmlFor="profile-company">
                Company name
                <RequiredMark aria-hidden="true">*</RequiredMark>
              </FieldLabel>
              <FormInput
                id="profile-company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={companyNameLocked}
              />
            </LabeledField>
            <FieldRow>
              <LabeledField>
                <FieldLabel
                  id="profile-department-label"
                  htmlFor={
                    isOther ? "profile-department-other" : "profile-department"
                  }
                >
                  Department
                  <RequiredMark aria-hidden="true">*</RequiredMark>
                </FieldLabel>
                {isOther ? (
                  <InputWithReset>
                    <FormInput
                      id="profile-department-other"
                      ref={otherDeptRef}
                      placeholder="Type your department"
                      value={customDepartment}
                      style={{ width: "100%", paddingRight: 30 }}
                      onChange={(e) => setCustomDepartment(e.target.value)}
                    />
                    <ResetButton
                      data-no-pill=""
                      aria-label="Choose from the list instead"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={resetDepartment}
                    >
                      <Icon name="x" size={12} />
                    </ResetButton>
                  </InputWithReset>
                ) : (
                  <SelectDropdown
                    id="profile-department"
                    labelId="profile-department-label"
                    value={department}
                    options={DEPARTMENTS}
                    onChange={setDepartment}
                  />
                )}
              </LabeledField>
              <LabeledField>
                <FieldLabel
                  id="profile-seniority-label"
                  htmlFor="profile-seniority"
                >
                  Role
                  <RequiredMark aria-hidden="true">*</RequiredMark>
                </FieldLabel>
                <SelectDropdown
                  id="profile-seniority"
                  labelId="profile-seniority-label"
                  value={seniority}
                  options={SENIORITIES}
                  onChange={setSeniority}
                />
              </LabeledField>
            </FieldRow>
            <ConsentGroup>
              <CheckRow
                role="checkbox"
                aria-checked={agreedMarketing}
                onPress={() => setAgreedMarketing((wasAgreed) => !wasAgreed)}
              >
                <CheckBoxSlot
                  data-check-box=""
                  data-checked={agreedMarketing ? "" : undefined}
                >
                  <Checkbox checked={agreedMarketing} />
                </CheckBoxSlot>
                <Text variant="text-small" color="var(--text-primary)">
                  Send me product updates and occasional marketing emails.
                </Text>
              </CheckRow>
            </ConsentGroup>
            <SubmitButton
              type="submit"
              variant="primary"
              size="l"
              disabled={!detailsComplete || leaving}
              style={{ ...fullWidthButton, marginTop: 16 }}
            >
              Get started
            </SubmitButton>
          </Form>
        </Content>
      </Surface>
    </Shell>
  );
}
